import type { Signal } from '@tb/shared';

// ---------------------------------------------------------------------------
// The house set of drawn marks: every icon the interface shows, one path each
// on a 24-unit grid, etched in a 1.6 stroke with square ends like the
// resource marks they grew from. A filled mark is solid where an outline would
// be too thin to read (a phase, the nuclear trefoil, the dots).
//
// Marks are named for what they mean, not what they look like, so one shape
// never stands for two things: the Attack phase, an all-out stance, a strike
// order, a long-range launcher and a winning city were all once the same
// triangle. The DOM draws them through <Icon> (ui/kit.tsx).
// ---------------------------------------------------------------------------

export interface IconDef { d: string; fill?: boolean }

export const ICONS = {
  // Moving about.
  close: { d: 'M6 6l12 12M18 6 6 18' },
  'chevron-left': { d: 'M15 5l-7 7 7 7' },
  'chevron-right': { d: 'M9 5l7 7-7 7' },
  'chevron-up': { d: 'M5 15l7-7 7 7' },
  'chevron-down': { d: 'M5 9l7 7 7-7' },
  'arrow-up': { d: 'M12 20V5M6 11l6-6 6 6' },
  'arrow-right': { d: 'M4 12h15M13 6l6 6-6 6' },
  'arrow-left': { d: 'M20 12H5M11 6l-6 6 6 6' },
  'arrow-out': { d: 'M7 17 17 7M9 7h8v8' },
  home: { d: 'M3 11.5 12 4l9 7.5M5.5 9.5V20h13V9.5M10 20v-5h4v5' },
  map: { d: 'M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20ZM9 4v13.5M15 6.5V20' },
  ledger: { d: 'M4 4h16v16H4ZM8 9h8M8 12.5h8M8 16h5' },
  target: { d: 'M12 6a6 6 0 1 0 0 12 6 6 0 1 0 0-12ZM12 2v5M12 17v5M2 12h5M17 12h5' },
  reach: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18ZM12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 1 0 0-9ZM12 11v2' },
  more: { d: 'M12 3.5a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 1 0 0-3.5ZM12 10.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 1 0 0-3.5ZM12 17a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 1 0 0-3.5Z', fill: true },
  // The phone ribbon's menu of the places opened now and then (ScaleNav).
  menu: { d: 'M4 6.5h16M4 12h16M4 17.5h16' },
  alert: { d: 'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.5 1.5h-15ZM10 20.5h4' },
  leave: { d: 'M10 4H4v16h6M15 8l4 4-4 4M19 12H9' },
  lock: { d: 'M7.5 11V8a4.5 4.5 0 0 1 9 0v3M5 11h14v9.5H5Z' },

  // Doing it again, and turning.
  repeat: { d: 'M4 11V9a3 3 0 0 1 3-3h12M16 3l3 3-3 3M20 13v2a3 3 0 0 1-3 3H5M8 21l-3-3 3-3' },
  'turn-left': { d: 'M4.48 9.26A8 8 0 1 1 4.48 14.74M1.48 6.26l3 3 3-3' },
  'turn-right': { d: 'M19.52 9.26A8 8 0 1 0 19.52 14.74M22.52 6.26l-3 3-3-3' },
  undo: { d: 'M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3' },
  heading: { d: 'M12 3l6.5 17L12 16l-6.5 4Z', fill: true },

  // Saying how things stand.
  check: { d: 'M4.5 12.5l4.5 4.5L19.5 6.5' },
  warning: { d: 'M12 3.5 2.5 20h19ZM12 10v5M12 16.8v1.4' },
  caution: { d: 'M12 4v11M12 18.2v1.6' },
  info: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18ZM12 10.5V17M12 7v1.6' },
  dot: { d: 'M12 9a3 3 0 1 0 0 6 3 3 0 1 0 0-6Z', fill: true },
  blocked: { d: 'M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18ZM5.6 5.6l12.8 12.8' },
  nuclear: {
    d: 'M10.5 9.4 8 5.07A8 8 0 0 1 16 5.07L13.5 9.4A3 3 0 0 0 10.5 9.4ZM15 12h5a8 8 0 0 1-4 6.93L13.5 14.6A3 3 0 0 0 15 12ZM10.5 14.6 8 18.93A8 8 0 0 1 4 12h5a3 3 0 0 0 1.5 2.6ZM12 10.6a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 1 0 0-2.8Z',
    fill: true,
  },
  special: { d: 'M12 3.5l2.5 5.6 6 .6-4.5 4 1.3 6-5.3-3.1-5.3 3.1 1.3-6-4.5-4 6-.6Z' },

  // The phases.
  plan: { d: 'M4 4h16v16H4ZM9 9h6v6H9Z' },
  attack: { d: 'M12 4 21 19H3Z', fill: true },
  defend: { d: 'M12 20 3 5h18Z', fill: true },
  resolution: { d: 'M12 3 21 12 12 21 3 12Z', fill: true },

  // A city's stance and condition.
  'all-out': { d: 'M5 13l7-7 7 7M5 19l7-7 7 7' },
  'dug-in': { d: 'M12 3 19.5 6v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6Z' },
  stopped: { d: 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9' },
  contaminated: { d: 'M12 3.5c3.6 4.4 5.5 7.6 5.5 10.5a5.5 5.5 0 0 1-11 0c0-2.9 1.9-6.1 5.5-10.5Z' },

  // Orders.
  strike: { d: 'M12 2.5c2 2 3 4.5 3 7.5v6H9v-6c0-3 1-5.5 3-7.5ZM9 13l-3 3v3l3-1.5M15 13l3 3v3l-3-1.5' },
  supply: { d: 'M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4' },

  // The air defences.
  ciws: { d: 'M6 19v-4a6 6 0 0 1 12 0v4ZM4 19h16M12 9V3' },
  'sam-short': { d: 'M12 6 16.5 19h-9Z' },
  'sam-long': { d: 'M12 3l6 17H6ZM8.6 13h6.8' },
  jammer: { d: 'M4 9c2.7-2.4 5.3-2.4 8 0s5.3 2.4 8 0M4 15c2.7-2.4 5.3-2.4 8 0s5.3 2.4 8 0' },
  shelter: { d: 'M4 19.5v-6a8 8 0 0 1 16 0v6ZM2.5 19.5h19M10 19.5V15h4v4.5' },

  // What a city asks of its side, or offers it.
  'air-defence': { d: 'M3.5 13a8.5 8.5 0 0 1 17 0ZM12 13v6.5a1.5 1.5 0 0 0 3 0' },
  munitions: { d: 'M9 20.5V10l3-6.5 3 6.5v10.5ZM9 16h6' },
  winning: { d: 'M3 17l6-6 4 4 7-7M14 8h6v6' },

  // The resources, the etched marks the set began with.
  materials: { d: 'M3 6h18v5H3V6m0 8h18v5H3v-5M8 6v5m8-5v5M12 14v5' },
  fuel: { d: 'M5 6h12l3 4v11H5V6m3 0V3h6v3M8 10l9 8m0-8-9 8m10-12 3-3' },
  credits: { d: 'm8 3-5 5v8l5 5h8l5-5V8l-5-5H8M9 8h7m-7 4h5m-5 4h7M9 8v8' },
} as const satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;

/** The mark for each status card a city can show its side: a need and an offer share one. */
export const SIGNAL_ICON: Record<Signal, IconName> = {
  NEED_DEFENCE: 'air-defence',
  SPARE_DEFENCE: 'air-defence',
  NEED_MUNITIONS: 'munitions',
  SPARE_MUNITIONS: 'munitions',
  WINNING: 'winning',
};
